import React from 'react';
import { isOverdueISO } from '../utils/accountingHelpers';
import { accountingApi } from '../api/accountingApi';

function formatLocalDateISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayISO() {
  return formatLocalDateISO(new Date());
}

function getMonthStartISO() {
  const d = new Date();
  d.setDate(1);
  return formatLocalDateISO(d);
}

function getMonthEndISO() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 0);
  return formatLocalDateISO(d);
}

function getYearStartISO() {
  const d = new Date();
  d.setMonth(0, 1);
  return formatLocalDateISO(d);
}

function getLast30DaysStartISO() {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return formatLocalDateISO(d);
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLocaleLowerCase('tr-TR')
    .trim();
}

function matchesIncomeSearch(row, q) {
  const s = normalizeSearchText(q);
  if (!s) return true;

  return [
    row.fullName,
    row.phone,
    row.payerName,
    row.payerPhone,
    row.note,
    row.activityName,
    row.paymentType,
    row.trainerName,
    row.memberId,
    row.trainerId,
  ].some((value) => normalizeSearchText(value).includes(s));
}

function matchesExpenseSearch(row, q) {
  const s = normalizeSearchText(q);
  if (!s) return true;

  return [row.title, row.category, row.note].some((value) =>
    normalizeSearchText(value).includes(s)
  );
}

const EMPTY_OVERVIEW_RESPONSE = {
  context: {
    from: null,
    to: null,
  },
  kpis: {
    totalReceivedTry: 0,
    totalPendingIncomeTry: 0,
    totalExpensePaidTry: 0,
    totalExpensePendingTry: 0,
    payrollMonthlyBaseTry: 0,
    netCashFlowTry: 0,
    pendingReceivablesCount: 0,
    pendingExpensesCount: 0,
    payrollReadyCount: 0,
    recentPaymentsCount: 0,
  },
  pendingReceivables: [],
  pendingExpenses: [],
  recentPayments: [],
  payrollReady: [],
};

const EMPTY_INCOME_RESPONSE = {
  context: {
    from: null,
    to: null,
    trainerId: null,
    q: null,
    limit: 0,
  },
  summary: {
    totalCollectedTry: 0,
    sessionCollectedTry: 0,
    packageCollectedTry: 0,
    generalCollectedTry: 0,
    expectedPaymentTry: 0,
    pendingCount: 0,
    collectedCount: 0,
  },
  collectedPayments: [],
  pendingPayments: [],
};

const EMPTY_EXPENSES_RESPONSE = {
  rows: [],
  summary: {
    totalCount: 0,
    pendingCount: 0,
    paidCount: 0,
    totalTry: 0,
    pendingTotalTry: 0,
    paidTotalTry: 0,
  },
};

export function useAccountingPage() {
  const [topTab, setTopTab] = React.useState(0);
  const [q, setQ] = React.useState('');
  const [onlyOverdue, setOnlyOverdue] = React.useState(true);

  const [datePreset, setDatePreset] = React.useState('THIS_MONTH');
  const [dateRange, setDateRange] = React.useState({
    startDate: getMonthStartISO(),
    endDate: getMonthEndISO(),
  });

  const [overviewResponse, setOverviewResponse] = React.useState(EMPTY_OVERVIEW_RESPONSE);
  const [overviewLoading, setOverviewLoading] = React.useState(false);
  const [overviewError, setOverviewError] = React.useState(null);

  const [incomeResponse, setIncomeResponse] = React.useState(EMPTY_INCOME_RESPONSE);
  const [paymentsLoading, setPaymentsLoading] = React.useState(false);
  const [paymentsError, setPaymentsError] = React.useState(null);

  const [expensesResponse, setExpensesResponse] = React.useState(EMPTY_EXPENSES_RESPONSE);
  const [expensesLoading, setExpensesLoading] = React.useState(false);
  const [expensesError, setExpensesError] = React.useState(null);

  React.useEffect(() => {
    if (datePreset === 'CUSTOM') return;

    if (datePreset === 'THIS_MONTH') {
      setDateRange({
        startDate: getMonthStartISO(),
        endDate: getMonthEndISO(),
      });
      return;
    }

    if (datePreset === 'LAST_30_DAYS') {
      setDateRange({
        startDate: getLast30DaysStartISO(),
        endDate: getTodayISO(),
      });
      return;
    }

    if (datePreset === 'THIS_YEAR') {
      setDateRange({
        startDate: getYearStartISO(),
        endDate: getTodayISO(),
      });
    }
  }, [datePreset]);

  const refetchOverview = React.useCallback(async () => {
    try {
      setOverviewLoading(true);
      setOverviewError(null);

      const res = await accountingApi.overview({
        from: dateRange.startDate,
        to: dateRange.endDate,
      });

      setOverviewResponse({
        context: res?.context || EMPTY_OVERVIEW_RESPONSE.context,
        kpis: {
          ...EMPTY_OVERVIEW_RESPONSE.kpis,
          ...(res?.kpis || {}),
        },
        pendingReceivables: Array.isArray(res?.pendingReceivables) ? res.pendingReceivables : [],
        pendingExpenses: Array.isArray(res?.pendingExpenses) ? res.pendingExpenses : [],
        recentPayments: Array.isArray(res?.recentPayments) ? res.recentPayments : [],
        payrollReady: Array.isArray(res?.payrollReady) ? res.payrollReady : [],
      });
    } catch (err) {
      setOverviewError(err);
      setOverviewResponse(EMPTY_OVERVIEW_RESPONSE);
    } finally {
      setOverviewLoading(false);
    }
  }, [dateRange.endDate, dateRange.startDate]);

  const refetchIncome = React.useCallback(async () => {
    try {
      setPaymentsLoading(true);
      setPaymentsError(null);

      const params = {
        from: dateRange.startDate,
        to: dateRange.endDate,
        limit: 300,
      };

      const res = await accountingApi.income(params);

      setIncomeResponse({
        context: res?.context || EMPTY_INCOME_RESPONSE.context,
        summary: {
          ...EMPTY_INCOME_RESPONSE.summary,
          ...(res?.summary || {}),
        },
        collectedPayments: Array.isArray(res?.collectedPayments) ? res.collectedPayments : [],
        pendingPayments: Array.isArray(res?.pendingPayments) ? res.pendingPayments : [],
      });
    } catch (err) {
      setPaymentsError(err);
      setIncomeResponse(EMPTY_INCOME_RESPONSE);
    } finally {
      setPaymentsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const refetchExpenses = React.useCallback(async () => {
    try {
      setExpensesLoading(true);
      setExpensesError(null);

      const res = await accountingApi.expenses({
        from: dateRange.startDate,
        to: dateRange.endDate,
      });

      setExpensesResponse({
        rows: Array.isArray(res?.rows) ? res.rows : [],
        summary: {
          totalCount: Number(res?.summary?.totalCount || 0),
          pendingCount: Number(res?.summary?.pendingCount || 0),
          paidCount: Number(res?.summary?.paidCount || 0),
          totalTry: Number(res?.summary?.totalTry || 0),
          pendingTotalTry: Number(res?.summary?.pendingTotalTry || 0),
          paidTotalTry: Number(res?.summary?.paidTotalTry || 0),
        },
      });
    } catch (err) {
      setExpensesError(err);
      setExpensesResponse(EMPTY_EXPENSES_RESPONSE);
    } finally {
      setExpensesLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  React.useEffect(() => {
    refetchOverview();
  }, [refetchOverview]);

  React.useEffect(() => {
    refetchIncome();
  }, [refetchIncome]);

  React.useEffect(() => {
    refetchExpenses();
  }, [refetchExpenses]);

  const pendingPaymentRows = React.useMemo(
    () => (Array.isArray(incomeResponse.pendingPayments) ? incomeResponse.pendingPayments : []),
    [incomeResponse.pendingPayments]
  );

  const collectedPaymentRows = React.useMemo(
    () => (Array.isArray(incomeResponse.collectedPayments) ? incomeResponse.collectedPayments : []),
    [incomeResponse.collectedPayments]
  );

  const pendingReceivables = React.useMemo(() => {
    let rows = pendingPaymentRows;

    rows = rows.filter((x) => matchesIncomeSearch(x, q));

    if (onlyOverdue) {
      rows = rows.filter((x) => {
        if (x.paymentType !== 'SESSION') return true;
        return x.dayISO ? isOverdueISO(x.dayISO) : true;
      });
    }

    return rows;
  }, [pendingPaymentRows, onlyOverdue, q]);

  const filteredPayments = React.useMemo(() => {
    return collectedPaymentRows.filter((x) => matchesIncomeSearch(x, q));
  }, [collectedPaymentRows, q]);

  const summary = React.useMemo(
    () => ({
      ...EMPTY_INCOME_RESPONSE.summary,
      ...(incomeResponse.summary || {}),
    }),
    [incomeResponse.summary]
  );

  const totalPendingIncome = React.useMemo(() => {
    if (!onlyOverdue) return Number(summary.expectedPaymentTry || 0);
    return pendingReceivables.reduce((sum, x) => sum + Number(x.amountTry || 0), 0);
  }, [pendingReceivables, onlyOverdue, summary.expectedPaymentTry]);

  const totalReceived = React.useMemo(
    () => Number(summary.totalCollectedTry || 0),
    [summary.totalCollectedTry]
  );

  const normalizedExpenses = React.useMemo(() => {
    return Array.isArray(expensesResponse.rows) ? expensesResponse.rows : [];
  }, [expensesResponse.rows]);

  const filteredExpenses = React.useMemo(() => {
    return normalizedExpenses.filter((e) => matchesExpenseSearch(e, q));
  }, [normalizedExpenses, q]);

  const pendingExpenses = React.useMemo(
    () => filteredExpenses.filter((e) => e.status === 'PENDING'),
    [filteredExpenses]
  );

  const totalExpensePaid = React.useMemo(
    () => Number(expensesResponse.summary?.paidTotalTry || 0),
    [expensesResponse.summary]
  );

  const totalExpensePending = React.useMemo(
    () => Number(expensesResponse.summary?.pendingTotalTry || 0),
    [expensesResponse.summary]
  );

  const createExpense = React.useCallback(
    async (payload) => {
      await accountingApi.createExpense(payload);
      await Promise.all([refetchExpenses(), refetchOverview()]);
    },
    [refetchExpenses, refetchOverview]
  );

  const markExpensePaid = React.useCallback(
    async (expense, payload) => {
      await accountingApi.markExpensePaid(expense.id, payload);
      await Promise.all([refetchExpenses(), refetchOverview()]);
    },
    [refetchExpenses, refetchOverview]
  );

  const markExpensePending = React.useCallback(
    async (expense, payload) => {
      await accountingApi.markExpensePending(expense.id, payload);
      await Promise.all([refetchExpenses(), refetchOverview()]);
    },
    [refetchExpenses, refetchOverview]
  );

  const deleteExpense = React.useCallback(
    async (expense) => {
      await accountingApi.deleteExpense(expense.id);
      await Promise.all([refetchExpenses(), refetchOverview()]);
    },
    [refetchExpenses, refetchOverview]
  );

  const overview = React.useMemo(() => {
    const kpis = {
      ...EMPTY_OVERVIEW_RESPONSE.kpis,
      ...(overviewResponse.kpis || {}),
    };

    const derivedPendingExpenses = pendingExpenses.slice(0, 8);
    const derivedRecentPayments = collectedPaymentRows.slice(0, 8);
    const derivedPendingReceivables = pendingReceivables.slice(0, 8);

    return {
      ...kpis,
      pendingReceivables: derivedPendingReceivables,
      pendingExpenses: derivedPendingExpenses,
      recentPayments: derivedRecentPayments,
      payrollReady: overviewResponse.payrollReady || [],
      pendingReceivablesCount: pendingReceivables.length,
      pendingExpensesCount: pendingExpenses.length,
      recentPaymentsCount: collectedPaymentRows.length,
      loading: overviewLoading,
      error: overviewError,
      refetch: refetchOverview,
    };
  }, [
    collectedPaymentRows,
    overviewError,
    overviewLoading,
    overviewResponse,
    pendingExpenses,
    pendingReceivables,
    refetchOverview,
  ]);

  const income = React.useMemo(
    () => ({
      summary,
      pendingPayments: pendingPaymentRows,
      collectedPayments: filteredPayments,
      filteredCustomers: pendingReceivables,
      filteredPayments,
      totalReceived,
      totalPendingIncome,
      loading: paymentsLoading,
      error: paymentsError,
      refetch: refetchIncome,
    }),
    [
      summary,
      pendingPaymentRows,
      filteredPayments,
      pendingReceivables,
      totalReceived,
      totalPendingIncome,
      paymentsLoading,
      paymentsError,
      refetchIncome,
    ]
  );

  const expensesData = React.useMemo(
    () => ({
      rows: filteredExpenses,
      totalExpensePaid,
      totalExpensePending,
      createExpense,
      markExpensePaid,
      markExpensePending,
      deleteExpense,
      loading: expensesLoading,
      error: expensesError,
      refetch: refetchExpenses,
    }),
    [
      filteredExpenses,
      totalExpensePaid,
      totalExpensePending,
      createExpense,
      markExpensePaid,
      markExpensePending,
      deleteExpense,
      expensesLoading,
      expensesError,
      refetchExpenses,
    ]
  );

  const payroll = React.useMemo(
    () => ({
      rows: overviewResponse.payrollReady || [],
    }),
    [overviewResponse.payrollReady]
  );

  return {
    topTab,
    setTopTab,
    q,
    setQ,
    onlyOverdue,
    setOnlyOverdue,
    datePreset,
    setDatePreset,
    dateRange,
    setDateRange,
    overview,
    income,
    expenses: expensesData,
    payroll,
  };
}
